import { cn } from '@/lib/utils';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

/** Flat, square-cornered buttons. Primary carries the accent; secondary is a hairline outline. */
export function Button({
  children,
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap font-medium transition-colors duration-150 rounded-sm border',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        variant === 'primary' && 'bg-accent text-paper border-accent hover:bg-accent-ink hover:border-accent-ink',
        variant === 'secondary' && 'bg-transparent text-ink border-line-strong hover:border-ink hover:bg-paper-3',
        variant === 'ghost' && 'bg-transparent text-ink-2 border-transparent hover:text-ink hover:border-line',
        size === 'sm' && 'px-3 py-1.5 text-xs gap-1.5',
        size === 'md' && 'px-4 py-2.5 text-sm gap-2',
        size === 'lg' && 'px-6 py-3 text-base gap-2.5',
        className
      )}
      {...props}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </button>
  );
}
